'use client'

import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ExamsMegaPanelProps {
  /** Server-rendered panel content (MegaPanelContent) */
  children: React.ReactNode
  /** Trigger label. Defaults to the Exams panel. */
  label?: string
  /** Unique id for the panel region (aria-controls target). */
  panelId?: string
}

const CLOSE_DELAY_MS = 60

/**
 * Desktop "Exams" mega-panel trigger. Opens on hover or click; Escape closes
 * and returns focus to the trigger; clicking a link inside or anywhere
 * outside closes it. The panel anchors to the navbar island (the nearest
 * positioned ancestor, set in NavShell).
 */
export function ExamsMegaPanel({
  children,
  label = 'Exams',
  panelId = 'exams-mega-panel',
}: ExamsMegaPanelProps) {
  const [open, setOpen] = useState<boolean>(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeTimer = useRef<number | null>(null)

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: MouseEvent): void => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      const focusWasInside =
        wrapperRef.current?.contains(document.activeElement) ?? false
      setOpen(false)
      if (focusWasInside) triggerRef.current?.focus()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open])

  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    }
  }, [])

  const cancelClose = (): void => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const scheduleClose = (): void => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS)
  }

  return (
    <div
      ref={wrapperRef}
      onPointerEnter={(event: React.PointerEvent) => {
        if (event.pointerType === 'mouse') {
          cancelClose()
          setOpen(true)
        }
      }}
      onMouseLeave={scheduleClose}
      onBlur={(event) => {
        if (!wrapperRef.current?.contains(event.relatedTarget as Node)) {
          setOpen(false)
        }
      }}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('a')) setOpen(false)
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(!open)}
        className="text-body hover:text-ink hover:bg-ink/[0.05] flex h-10 items-center gap-1 rounded-full px-3 text-sm font-medium transition-colors"
      >
        {label}
        <ChevronDown
          size={14}
          className={cn(
            'transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && (
        <div className="absolute inset-x-0 top-full pt-2">
          <div
            id={panelId}
            className="border-border bg-bg shadow-float mx-auto max-w-4xl overflow-hidden rounded-2xl border"
          >
            {children}
          </div>
        </div>
      )}
    </div>
  )
}
